/**
 * Authentication Service
 *
 * Handles user authentication via Supabase (Google OAuth).
 * Provides React context and hooks for auth state.
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { syncManager } from './syncManager';
import { clearLocalDataOnSignOut } from './sync';
import { startScheduledRunner, stopScheduledRunner } from './scheduledRunner';
import { getDevFakeSession, isDevFakeAuth } from './devAuth';
import { checkAppAccess } from './appAccess';

// ============================================================================
// Pro Access Check (Sync Gating)
// ============================================================================

// Pro bypass - only these get free Pro (teslax707 must pay to test flow)
const PRO_BYPASS_EMAILS = ['nova223@gmail.com'];

/**
 * Check if user has Pro access for sync features.
 * Pro = bypass email OR active/lifetime/trialing subscription.
 */
function checkProAccess(user: User | null): boolean {
  if (!user) return false;
  
  // Pro bypass (only nova223, not all owners)
  const email = user.email?.toLowerCase();
  if (email && PRO_BYPASS_EMAILS.includes(email)) {
    console.log('[AUTH] Pro access: owner bypass');
    return true;
  }
  
  // Check subscription status in user metadata
  const status = user.user_metadata?.subscriptionStatus;
  if (status === 'active' || status === 'lifetime' || status === 'trialing') {
    console.log('[AUTH] Pro access: subscription', status);
    return true;
  }
  
  console.log('[AUTH] Free user - sync disabled');
  return false;
}

// ============================================================================
// Types
// ============================================================================

export interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface AuthContextValue extends AuthState {
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  signOutEverywhere: () => Promise<{ error: AuthError | null }>;
  getUserId: () => string | null;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track previous user to detect login vs already-logged-in
  const prevUserRef = useRef<User | null>(null);

  // BUG-061 FIX: Track if we've triggered login sync this session
  // Prevents double-firing from multiple auth state change events
  const hasTriggeredLoginSyncRef = useRef(false);

  // Initialize auth state
  useEffect(() => {
    console.log('[AUTH] Initializing auth state...');

    // DEV FAKE AUTH (localhost dev only - see services/devAuth.ts gates).
    // Skips Supabase entirely so local dev/testing needs no Google OAuth.
    const fakeSession = getDevFakeSession();
    if (fakeSession) {
      setSession(fakeSession);
      setUser(fakeSession.user);
      setIsLoading(false);
      return;
    }

    let active = true;
    let validationId = 0;

    const applySession = async (nextSession: Session | null, eventName: string) => {
      const currentValidation = ++validationId;
      if (!nextSession) {
        if (!active || currentValidation !== validationId) return;
        setSession(null);
        setUser(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const access = await checkAppAccess(nextSession);
      if (!active || currentValidation !== validationId) return;
      if (!access.allowed) {
        console.warn('[AUTH] Session denied by app access policy:', access.reason, eventName);
        setSession(null);
        setUser(null);
        setIsLoading(false);
        window.setTimeout(() => {
          void supabase.auth.signOut({ scope: 'local' });
        }, 0);
        return;
      }

      setSession(nextSession);
      setUser(nextSession.user);
      setIsLoading(false);
    };

    // Get initial session and validate it against Private Mode.
    void supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      console.log('[AUTH] Initial session:', initialSession ? 'found' : 'none');
      return applySession(initialSession, 'INITIAL_SESSION');
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      console.log('[AUTH] Auth state changed:', _event);
      void applySession(newSession, _event);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  // Initialize sync manager and scheduled runner when user changes
  useEffect(() => {
    const wasLoggedOut = !prevUserRef.current;
    const isNowLoggedIn = !!user;
    const isPro = checkProAccess(user);

    if (user) {
      // Only initialize sync manager for Pro/Trial/Lifetime users
      if (isPro) {
        // Initialize sync manager with user context + sync access flag (BUG-065 defense in depth)
        syncManager.init(user.id, user.email ?? null, true);

        // BUG-061 FIX: Only trigger login sync ONCE per session
        // Multiple auth state changes can fire (SIGNED_IN, INITIAL_SESSION, etc.)
        // We only want ONE login sync, not multiple
        if (wasLoggedOut && isNowLoggedIn && !hasTriggeredLoginSyncRef.current) {
          console.log('[AUTH] Fresh login detected - triggering sync (once)');
          hasTriggeredLoginSyncRef.current = true;
          syncManager.onLogin();
        }
      } else {
        // Free user - ensure sync manager is cleaned up (no syncing)
        syncManager.cleanup();
      }

      // Start scheduled transaction runner (works for all users, local-only)
      startScheduledRunner();
    } else {
      // Cleanup on logout
      syncManager.cleanup();
      stopScheduledRunner();

      // BUG-061: Reset login sync flag so next login triggers sync
      hasTriggeredLoginSyncRef.current = false;
    }

    prevUserRef.current = user;
  }, [user]);

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    console.log('[AUTH] Signing in with Google...');
    setIsLoading(true);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      console.error('[AUTH] Google sign-in error:', error.message);
      setIsLoading(false);
    }

    return { error };
  }, []);

  // Sign out (this browser only)
  const signOut = useCallback(async () => {
    console.log('[AUTH] Signing out (this browser)...');
    setIsLoading(true);

    // CRITICAL: Clear local data before signing out to prevent data leakage
    // If another user signs in, they shouldn't see the previous user's data
    await clearLocalDataOnSignOut();

    // DEV FAKE AUTH: no Supabase session exists - just clear local state.
    // (Reload signs the fake user back in; disable via VITE_DEV_FAKE_AUTH.)
    if (isDevFakeAuth()) {
      console.warn('[AUTH] Dev fake auth: local data cleared; fake session resumes on reload');
      setUser(null);
      setSession(null);
      setIsLoading(false);
      return { error: null };
    }

    // Use scope: 'local' to only invalidate THIS session, not all sessions across devices
    // This prevents local dev signout from breaking production sessions (same Supabase instance)
    const { error } = await supabase.auth.signOut({ scope: 'local' });

    if (error) {
      // Log error but don't fail - user wants to be signed out locally regardless
      // Common cause: session already invalidated by another device (scope: global elsewhere)
      console.error('[AUTH] Sign-out error (proceeding anyway):', error.message);
    }

    // Always clear local auth state even if server signout failed
    // User clicked "sign out" - they want to be signed out locally
    setUser(null);
    setSession(null);
    setIsLoading(false);
    return { error };
  }, []);

  // Sign out from ALL devices (global)
  const signOutEverywhere = useCallback(async () => {
    console.log('[AUTH] Signing out from ALL devices...');
    setIsLoading(true);

    // Clear local data first
    await clearLocalDataOnSignOut();

    // Use scope: 'global' to invalidate ALL sessions across all devices
    // Use case: lost phone, security concern, want to force re-login everywhere
    const { error } = await supabase.auth.signOut({ scope: 'global' });

    if (error) {
      console.error('[AUTH] Sign-out everywhere error (proceeding anyway):', error.message);
    }

    // Always clear local auth state
    setUser(null);
    setSession(null);
    setIsLoading(false);
    return { error };
  }, []);

  // Get user ID (for data isolation)
  const getUserId = useCallback(() => {
    return user?.id ?? null;
  }, [user]);

  const value: AuthContextValue = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signInWithGoogle,
    signOut,
    signOutEverywhere,
    getUserId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { AuthContext };
